import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import React, { useRef } from 'react';
import classNames from 'classnames';
import { getThemeClassNames, useSlots, useCombinedRef, useWidth } from '@cognite/cogs-utils';
import { TopbarLogo, TopbarLeft, TopbarRight, AppSwitcher, AppSwitcherItem, TopbarButton, TopbarLink, TopbarSearch } from './elements.js';
import { TopbarContext } from './top-bar-context.js';

/**
 * Topbar
 * =============================================================================
 * Describe what your component does, and why it could be useful for users.
 *
 * [Open docs »](cognitedata.github.io/cogs.js/?path=/docs/components-topbar--docs)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Topbar>
 *  <Topbar.Left>
 *    <Topbar.AppSwitcher>{apps.map((app) => (...))}</Topbar.AppSwitcher>
 *  </Topbar.Left>
 *  <Topbar.Right>...</Topbar.Right>
 * </Topbar>
 *
 * <Topbar inverted>
 *  <Topbar.Left>...</Topbar.Left>
 *  <Topbar.Right>...</Topbar.Right>
 * </Topbar>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param subject - Explain here what your prop does
 *
 * [Read the full documentation »](cognitedata.github.io/cogs.js/?path=/docs/components-topbar--docs)
 */
var Topbar = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var children = _a.children,
    inverted = _a.inverted,
    themeProps = _a.themeProps,
    props = __rest(_a, ["children", "inverted", "themeProps"]);
  var extendedClassNames = classNames.apply(undefined, ['cogs-v10', 'cogs-topbar'].concat(getThemeClassNames(inverted, themeProps), [props.className]));
  var _useSlots = useSlots(children, {
      left: TopbarLeft,
      right: TopbarRight
    }),
    slots = _useSlots[0],
    slotsWithoutProps = _useSlots[1];
  var innerRef = useRef(null);
  var combinedRef = useCombinedRef(ref, innerRef);
  var width = useWidth(innerRef);
  var contextValue = React.useMemo(function () {
    return {
      width: width
    };
  }, [width]);
  return jsx(TopbarContext.Provider, {
    value: contextValue,
    children: jsxs("nav", Object.assign({
      className: extendedClassNames,
      ref: combinedRef
    }, props, {
      children: [slots.left, slotsWithoutProps, slots.right]
    }))
  });
});
Topbar.displayName = 'Topbar';
var TopbarInner = Object.assign(Topbar, {
  Logo: TopbarLogo,
  Left: TopbarLeft,
  Right: TopbarRight,
  AppSwitcher: AppSwitcher,
  AppSwitcherItem: AppSwitcherItem,
  Button: TopbarButton,
  Link: TopbarLink,
  Search: TopbarSearch
});

export { TopbarInner as Topbar };
